import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { RootState, AppDispatch } from "../../Redux/store";
import { getUserDetails } from "../../Redux/slices/userSlice";
import { createMessageChannel } from "../../Redux/slices/messageSlice";
import { verifyAndSubscribe } from "../../Redux/slices/subscriptionSlice";
import { getCreatorProfilePath } from "../../Utils";

const PaymentVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const user = useSelector((state: RootState) => state.user);
  const { verifying } = useSelector((state: RootState) => state.subscription);

  const subId = searchParams.get("sub_id");
  const creatorIdFromQuery = searchParams.get("creator_id");
  const username = searchParams.get("username");

  const helperText =
    elapsedSeconds >= 25
      ? "Payment received. Membership access is still syncing."
      : "This may take a few seconds. Please don't close this tab.";

  useEffect(() => {
    const interval = window.setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!subId) {
      toast.error("Missing subscription ID");
      navigate("/");
      return;
    }

    const handleVerification = async () => {
      if (!user?.id) {
        toast.error("User not authenticated.");
        navigate("/login");
        return;
      }

      const userId = user.id;
      const profilePath = username ? getCreatorProfilePath(username) : "/";

      const result = await dispatch(
        verifyAndSubscribe({
          subscriptionId: subId,
          creatorId: creatorIdFromQuery || "",
        })
      );

      if (verifyAndSubscribe.fulfilled.match(result)) {
        navigate(profilePath);

        const resolvedCreatorId =
          creatorIdFromQuery || result.payload.creatorId;

        void (async () => {
          await dispatch(getUserDetails());

          if (resolvedCreatorId) {
            dispatch(
              createMessageChannel({
                from: userId,
                to: resolvedCreatorId,
                type: "subscribed",
              })
            );
          }
        })();
      } else {
        navigate(profilePath);
      }
    };

    handleVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId, creatorIdFromQuery, username, user?.id, dispatch, navigate]);

  return (
    <VerificationShell>
      <VerificationContent>
        <h2>
          {verifying ? "Verifying your subscription" : "Redirecting"}
          <Dots aria-hidden="true">
            <span />
            <span />
            <span />
          </Dots>
        </h2>
        <p>{helperText}</p>
      </VerificationContent>
    </VerificationShell>
  );
};

const dotPulse = keyframes`
  0%, 80%, 100% {
    opacity: 0.24;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
`;

const VerificationShell = styled.div`
  align-items: center;
  background: #000;
  color: #fff;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  text-align: center;
`;

const VerificationContent = styled.div`
  max-width: 560px;
  width: 100%;

  h2 {
    align-items: baseline;
    display: inline-flex;
    font-size: 28px;
    font-weight: 500;
    justify-content: center;
    line-height: 1.25;
    margin: 0 0 18px;
  }

  p {
    color: rgba(255, 255, 255, 0.86);
    font-size: 17px;
    line-height: 1.5;
    margin: 0;
  }

  @media (max-width: 640px) {
    h2 {
      font-size: 24px;
    }

    p {
      font-size: 15px;
    }
  }
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 4px;
  margin-left: 4px;

  span {
    animation: ${dotPulse} 1.35s infinite ease-in-out;
    background: currentColor;
    border-radius: 999px;
    height: 4px;
    width: 4px;
  }

  span:nth-child(2) {
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

export default PaymentVerification;
